import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Brain, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Terms of Service - MIND FRAME';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-800">MIND FRAME</span>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </nav>
      </header>

      {/* Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 md:p-12 border border-blue-100">
            <h1 className="text-4xl font-bold text-slate-800 mb-4">MIND FRAME – Terms of Service</h1>
            <p className="text-slate-600 mb-8"><strong>Effective Date:</strong> March 26th, 2026</p>
            
            <p className="text-slate-700 leading-relaxed mb-8">
              By accessing or using MIND FRAME services, you agree to the following terms:
            </p>

            <hr className="my-8 border-slate-200" />

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">1. Nature of Services</h2>
              <p className="text-slate-700 mb-3">MIND FRAME provides counselling and emotional support services.</p>
              <p className="text-slate-700 mb-3">These services:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Are not medical or psychiatric treatment</li>
                <li>Do not involve diagnosis or prescription</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">2. Eligibility</h2>
              <p className="text-slate-700 mb-2">You must be at least 18 years old to use the services.</p>
              <p className="text-slate-700">If under 18, parental/guardian consent is required.</p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">3. User Responsibilities</h2>
              <p className="text-slate-700 mb-3">You agree to:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Provide accurate information</li>
                <li>Engage respectfully</li>
                <li>Not misuse the platform</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">4. No Emergency Services</h2>
              <p className="text-slate-700 mb-3">MIND FRAME is not an emergency service.</p>
              <p className="text-slate-700 mb-3">In case of crisis, contact:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Local emergency services</li>
                <li>A licensed psychiatrist</li>
                <li>Mental health helplines</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">5. Payments & Refunds</h2>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>All sessions must be paid in advance</li>
                <li>Cancellations must be made at least <strong>24 hours</strong> prior</li>
                <li>No-shows or late cancellations may not be refunded</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">6. Limitation of Liability</h2>
              <p className="text-slate-700 mb-3">MIND FRAME is not liable for:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Decisions made by clients</li>
                <li>Outcomes of counselling sessions</li>
                <li>Any indirect or incidental damages</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">7. Confidentiality</h2>
              <p className="text-slate-700">
                We maintain strict confidentiality subject to legal and ethical exceptions.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">8. Intellectual Property</h2>
              <p className="text-slate-700">
                All content on this platform belongs to MIND FRAME and may not be copied or reused without permission.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">9. Termination</h2>
              <p className="text-slate-700 mb-3">We reserve the right to terminate services in case of:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Misuse</li>
                <li>Abuse or inappropriate behavior</li>
              </ul>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">10. Governing Law</h2>
              <p className="text-slate-700">
                These terms are governed by the laws of India.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">11. Consent</h2>
              <p className="text-slate-700">
                By using our services, you agree to these Terms of Service.
              </p>
            </section>

            <hr className="my-8 border-slate-200" />

            {/* Footer */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-slate-800 font-medium">
                By using MIND FRAME, you accept and agree to these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400">© 2024 MIND FRAME. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
